import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import styled, { css } from "styled-components";
import Container from "./Container";
import Title from "./Title";
import { transformImageUrl } from "./SafeSiteImage";
import { Content, HtmlContent } from "./Content";
import { Carousel } from "./Carousel";

const HeroLandingPageStyles = styled.section`
  ${({ ...props }) => css`
    padding: 64px 0;
    background-color: ${'grey' === props.$background ? props.theme.colors.lightGrey : '#fff'};
    background-image: ${('image' === props.$background || 'video' === props.$background) && props.$mobileBackgroundImage ? `url(${props.$mobileBackgroundImage})` : ''};
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    position: relative;
    aspect-ratio: 16/9;

    &:before {
      content: ${('image' === props.$background || 'video' === props.$background ? '\'\'' : 'none')};
      position: absolute;
      inset: 0;
      height: 100%;
      width: 100%;
      background: ${props.$overlayColour ?? '#000'};
      opacity: ${props.$overlayOpacity ?? 0.1};
      z-index: 1;
    }

    div {
      z-index: 2
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      padding: 128px 0;
      aspect-ratio: auto;
      min-height: ${(props.$fullHeight ? '68vh' : '')};
      max-width: 100%;
    }

    @media (min-width: ${props.theme.breakpoints.xl}) {
      background-image: ${'image' === props.$background || 'video' === props.$background ? `url(${props.$desktopBackgroundImage})` : ''};
    }
  `}
`;

const HeroLandingPageTextStyles = styled.div`
  ${({ ...props }) => css`
    text-align: center;

    .heading,
    & {
      color: ${(props.$darkText ? props.theme.darkGrey : '#fff')};
    }

    .heading {
      text-transform: ${(props.$uppercaseTitle ? 'uppercase' : 'none')};
      font-size: 1.75rem;
      line-height: 32px;
      text-shadow: ${props.$textShadow.x ?? 0}px ${props.$textShadow.y ?? 0}px ${props.$textShadow.blur ?? 40}px rgba(0, 0, 0, ${props.$textShadow.opacity ?? .7});
      @media (min-width: ${props.theme.breakpoints.md}) {
        font-size: ${(props.$fullHeight ? '54px' : '36px')};
        line-height: ${(props.$fullHeight ? '72px' : '48px')};
      }
    }

    .content {
      font-size: ${(props.$fullHeight ? '1rem' : '0.875rem')};
    }

    .content-wrapper {
      display: none;
      @media (min-width: ${props.theme.breakpoints.md}) {
        display: block;
      }
    }

    div:not(:last-child):has(.heading) {
      margin-bottom: .5rem
    }

    .button {
      margin-top: 1rem
    }
  `}
`;

const HeroLandingPageVideoStyles = styled.div`
  ${({ ...props }) => css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 0 !important;
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center center;
    }
  `}
`;

const HeroLandingPageMobileContentStyles = styled.div`
  ${({ ...props }) => css`
    display: block;
    text-align: center;
    padding: 2rem 0;
    background-color:  ${'grey' === props.$mobileBackground ? props.theme.colors.lightGrey : '#fff'};

    &:has(> div:empty) {
      display: none
    }

    > div {
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 1rem;
    }

    .content {
      font-size: 1rem;
    }

    @media (min-width: ${props.theme.breakpoints.md}) {
      display: none;
    }
  `}
`;

const HeroLandingPageCarouselStyles = styled.div`
  ${({ ...props }) => css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 0 !important;

    .swiper-container, .swiper-wrapper {
      height: 100%;
    }
  `}
`

const defaultTextShadow = { x: 0, y: 0, blur: 40, opacity: .7 }

export const HeroLandingPage = ({
  title,
  content,
  button,
  desktopImage,
  mobileImage,
  fullHeight,
  overlayColour,
  overlayOpacity,
  video,
  videoPoster,
  background = 'white',
  mobileBackground = 'white',
  uppercaseTitle,
  darkText,
  textShadow = defaultTextShadow,
  carouselImages,
  autoScroll,
  delay,
}) => {
  if (!textShadow) {
    textShadow = defaultTextShadow
  }
  let site = {
    siteMetadata: {
      siteUrl: 'https://safesitefacilities.co.uk'
    }
  }
  if (!process.env.STORYBOOK) {
    ({ site } = useStaticQuery(graphql`
      query SiteUrlQuery {
        site {
          siteMetadata {
            siteUrl
          }
        }
      }
    `))
  }

  const mobileBackgroundImage = mobileImage ? transformImageUrl({ siteUrl: site.siteMetadata.siteUrl, imageUrl: mobileImage, width: 1400 }) : null;
  const desktopBackgroundImage = desktopImage ? transformImageUrl({ siteUrl: site.siteMetadata.siteUrl, imageUrl: desktopImage, width: 1920 }) : null;

  return (
    <>
      <HeroLandingPageStyles
        $mobileBackgroundImage={mobileBackgroundImage}
        $desktopBackgroundImage={desktopBackgroundImage}
        $overlayColour={overlayColour}
        $overlayOpacity={overlayOpacity}
        $fullHeight={fullHeight}
        $background={background}
      >
        {background === 'video' && (
          <HeroLandingPageVideoStyles>
            <video autoPlay muted loop playsInline poster={transformImageUrl({ siteUrl: site.siteMetadata.siteUrl, imageUrl: videoPoster?.mediaItemUrl, width: 1920 })}>
              <source src={video}></source>
            </video>
          </HeroLandingPageVideoStyles>
        )}
        {background === 'carousel' && (
          <HeroLandingPageCarouselStyles>
            <Carousel
              images={carouselImages}
              fullWidth={true}
              autoScroll={autoScroll}
              delay={delay}
            />
          </HeroLandingPageCarouselStyles>
        )}
        <Container slim>
          <HeroLandingPageTextStyles
            $uppercaseTitle={uppercaseTitle}
            $darkText={darkText}
            $fullHeight={fullHeight}
            $textShadow={textShadow}
          >
            <Title title={title} classes="heading" />
            <div className="content-wrapper">
              {content &&
                <div className="content">
                  <Content content={content} />
                </div>
              }
              {button?.url && button?.title && (
                <a href={button.url} target={button.target} className="button">
                  <HtmlContent htmlString={button.title} />
                </a>
              )}
            </div>
          </HeroLandingPageTextStyles>
        </Container>
      </HeroLandingPageStyles>
      <HeroLandingPageMobileContentStyles
        $mobileBackground={mobileBackground}
      >
        <Container>
            {content &&
              <div className="content">
                <Content content={content} />
              </div>
            }
            {button?.url && button?.title && (
              <a href={button.url} target={button.target} className="button">
                <HtmlContent htmlString={button.title} />
              </a>
            )}
        </Container>
      </HeroLandingPageMobileContentStyles>
    </>
  );
};
